import {
  resourceKeys,
  rosterKeys,
  useCreateResource,
  useDriverRosterConfigs,
  useResources,
  useRosterGroups,
  useSitesWithMembers,
  useUpdateResource,
  useUpsertDriverRosterConfig,
} from '@my/directory-api/hooks';
import type { RosterScheduleType, RosterWorkingPattern } from '@my/directory-api';
import { directoryApi } from '@my/directory-api';
import { ErrorBanner, SelectInput, showToast, Switch, TextInput } from '@my/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Edit, Loader2, Plus, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { runPool } from '@/features/parties/import/lib/commitImport';
import { EMPTY_BULK, type BulkRosterValues } from '../lib/bulkRoster';
import { BulkRosterBar } from './BulkRosterBar';

type Status = 'Active' | 'Inactive';

const PATTERN_LABELS: Record<RosterWorkingPattern, string> = {
  MondayToFriday: 'Monday - Friday',
  MondayToSaturday: 'Monday - Saturday',
  TuesdayToSaturday: 'Tuesday - Saturday',
  SundayToThursday: 'Sunday - Thursday',
  TwoWeekRotation: 'Two-week rotation',
};
const PATTERNS = Object.keys(PATTERN_LABELS) as RosterWorkingPattern[];

const ROW = 'grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4';

interface Row {
  resourceId: string;
  name: string;
  displayName: string;
  reference: string;
  mobileNumber: string;
  email: string;
  status: Status;
  hasConfig: boolean;
  rosterGroupId: string | null;
  groupName: string | null;
  homeSiteId: string | null;
  startLocation: string | null;
  defaultStartTime: string | null;
  scheduleType: RosterScheduleType;
  workingPattern: RosterWorkingPattern;
  phaseEvenWeekStart: boolean;
}

interface Draft {
  name: string;
  displayName: string;
  reference: string;
  mobileNumber: string;
  email: string;
  status: Status;
  rosterGroupId: string;
  homeSiteId: string;
  startLocation: string;
  defaultStartTime: string;
  scheduleType: RosterScheduleType;
  workingPattern: RosterWorkingPattern;
  phaseEvenWeekStart: boolean;
}

const EMPTY: Draft = {
  name: '',
  displayName: '',
  reference: '',
  mobileNumber: '',
  email: '',
  status: 'Active',
  rosterGroupId: '',
  homeSiteId: '',
  startLocation: '',
  defaultStartTime: '',
  scheduleType: 'FullTime',
  workingPattern: 'MondayToFriday',
  phaseEvenWeekStart: false,
};

function rowToDraft(r: Row): Draft {
  return {
    name: r.name,
    displayName: r.displayName,
    reference: r.reference,
    mobileNumber: r.mobileNumber,
    email: r.email,
    status: r.status,
    rosterGroupId: r.rosterGroupId ?? '',
    homeSiteId: r.homeSiteId ?? '',
    startLocation: r.startLocation ?? '',
    defaultStartTime: r.defaultStartTime ?? '',
    scheduleType: r.scheduleType,
    workingPattern: r.workingPattern,
    phaseEvenWeekStart: r.phaseEvenWeekStart,
  };
}

/** `HH:mm` → `HH:mm:ss` for the wire; blank → null. */
function hms(v: string): string | null {
  const t = v.trim();
  if (!t) return null;
  return t.length === 5 ? `${t}:00` : t;
}
const orNull = (s: string) => (s.trim() ? s.trim() : null);

/**
 * Drivers tab — a Resources-style grid scoped to Driver resources (no
 * compliance). Lists every Driver merged with its roster config; "+ Add" / row
 * Edit open an inline editor (tick / cross). The editor carries the resource
 * basics (name, contact, status) plus the roster config (group, depot, start,
 * pattern, schedule); add creates the resource then its config, edit updates both.
 */
export function DriversGrid({ organisationId }: { organisationId: string | null }) {
  const resourcesQuery = useResources(organisationId);
  const configsQuery = useDriverRosterConfigs(organisationId);
  const groupsQuery = useRosterGroups(organisationId);
  const sitesQuery = useSitesWithMembers(organisationId);
  const createResource = useCreateResource();
  const updateResource = useUpdateResource();
  const upsertConfig = useUpsertDriverRosterConfig();
  const isBusy = createResource.isPending || updateResource.isPending || upsertConfig.isPending;

  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [search, setSearch] = useState('');
  // Bulk roster onboarding — select many drivers, apply one set of settings.
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [bulk, setBulk] = useState<BulkRosterValues>(EMPTY_BULK);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);

  const groups = groupsQuery.data ?? [];
  const sites = useMemo(
    () => (sitesQuery.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [sitesQuery.data],
  );
  const siteNameById = useMemo(() => new Map(sites.map((s) => [s.id, s.name])), [sites]);

  const rows = useMemo<Row[]>(() => {
    const configByResource = new Map((configsQuery.data ?? []).map((c) => [c.resourceId, c]));
    return (resourcesQuery.data ?? [])
      .filter((r) => r.type === 'Driver')
      .map((d) => {
        const c = configByResource.get(d.id);
        return {
          resourceId: d.id,
          name: d.name,
          displayName: d.displayName ?? '',
          reference: d.reference ?? '',
          mobileNumber: d.mobileNumber ?? '',
          email: d.email ?? '',
          status: d.status as Status,
          hasConfig: !!c,
          rosterGroupId: c?.rosterGroupId ?? null,
          groupName: c?.groupName ?? null,
          // Depot lives on the Resource now (single source of truth for every
          // resource type), not on the roster config.
          homeSiteId: d.homeSiteId ?? null,
          startLocation: c?.startLocation ?? null,
          defaultStartTime: c?.defaultStartTime ?? null,
          scheduleType: c?.scheduleType ?? 'FullTime',
          workingPattern: c?.workingPattern ?? 'MondayToFriday',
          phaseEvenWeekStart: c?.phaseEvenWeekStart ?? false,
        };
      });
  }, [resourcesQuery.data, configsQuery.data]);

  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    let out = rows;
    if (q) {
      out = out.filter((r) =>
        [r.displayName || r.name, r.groupName ?? '', r.startLocation ?? '', r.email]
          .join(' ')
          .toLowerCase()
          .includes(q),
      );
    }
    if (onlyMissing) out = out.filter((r) => !r.hasConfig);
    return out;
  }, [rows, q, onlyMissing]);

  const missingCount = useMemo(() => rows.filter((r) => !r.hasConfig).length, [rows]);

  // ── Selection ──────────────────────────────────────────────────────────────

  const rowById = useMemo(() => new Map(rows.map((r) => [r.resourceId, r])), [rows]);
  const selectedRows = useMemo(
    () => [...selected].map((id) => rowById.get(id)).filter((r): r is Row => !!r),
    [selected, rowById],
  );
  const selectedNewCount = selectedRows.filter((r) => !r.hasConfig).length;
  const allFilteredSelected =
    filtered.length > 0 && filtered.every((r) => selected.has(r.resourceId));

  const toggleRow = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllFiltered = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) for (const r of filtered) next.delete(r.resourceId);
      else for (const r of filtered) next.add(r.resourceId);
      return next;
    });

  const clearSelection = () => {
    setSelected(new Set());
    setBulk(EMPTY_BULK);
  };

  /**
   * Apply the bulk settings to every selected driver. Each blank bulk field
   * falls back to the driver's existing value, so this never wipes a config that
   * was already set up — and because the endpoint is an upsert, drivers without
   * a config get one created (which is what lands them on the Roster grid).
   */
  const applyBulk = async () => {
    if (!organisationId || selectedRows.length === 0) return;
    setBulkProgress({ done: 0, total: selectedRows.length });

    let ok = 0;
    const failures: string[] = [];

    // Depot is a Resource field, so it goes through the dedicated bulk endpoint
    // in one request rather than riding along with the per-driver config upserts.
    if (bulk.homeSiteId) {
      try {
        await directoryApi.assignResourcesHomeSite({
          organisationId,
          resourceIds: selectedRows.map((r) => r.resourceId),
          homeSiteId: bulk.homeSiteId,
        });
      } catch (err) {
        failures.push(`Depot: ${err instanceof Error ? err.message : 'failed'}`);
      }
    }

    await runPool(
      selectedRows,
      async (r) => {
        try {
          await directoryApi.upsertDriverRosterConfig({
            organisationId,
            resourceId: r.resourceId,
            rosterGroupId: bulk.rosterGroupId || r.rosterGroupId || null,
            startLocation: bulk.startLocation.trim() || r.startLocation || null,
            defaultStartTime: hms(bulk.defaultStartTime) ?? hms(r.defaultStartTime ?? ''),
            scheduleType: bulk.scheduleType || r.scheduleType,
            workingPattern: bulk.workingPattern || r.workingPattern,
            phaseEvenWeekStart: r.phaseEvenWeekStart,
            jobType: null,
          });
          ok += 1;
        } catch (err) {
          failures.push(
            `${r.displayName || r.name}: ${err instanceof Error ? err.message : 'failed'}`,
          );
        }
        setBulkProgress((p) => (p ? { ...p, done: p.done + 1 } : p));
      },
      5,
    );

    // One refresh for the batch — repopulates the grid and the Roster tab.
    // Resources too, since the depot assignment writes there.
    await qc.invalidateQueries({ queryKey: rosterKeys.all });
    await qc.invalidateQueries({ queryKey: resourceKeys.byOrganisation(organisationId) });
    setBulkProgress(null);

    if (failures.length === 0) {
      showToast({ type: 'success', message: `${ok} driver${ok === 1 ? '' : 's'} added to the roster.` });
      clearSelection();
    } else {
      showToast({
        type: 'error',
        message: `${ok} updated, ${failures.length} failed. ${failures[0] ?? ''}`,
      });
    }
  };

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const startNew = () => {
    setEditingId('new');
    setDraft(EMPTY);
  };
  const startEdit = (r: Row) => {
    setEditingId(r.resourceId);
    setDraft(rowToDraft(r));
  };
  const cancel = () => {
    setEditingId(null);
    setDraft(EMPTY);
  };

  const save = async () => {
    if (!organisationId || editingId === null) return;
    const name = draft.name.trim();
    if (!name) return;

    const resourceFields = {
      type: 'Driver' as const,
      name,
      displayName: orNull(draft.displayName),
      mobileNumber: orNull(draft.mobileNumber),
      email: orNull(draft.email),
      reference: orNull(draft.reference),
      organisationEquipmentTypeId: null,
      // Depot is a Resource field now — the roster config no longer carries it.
      homeSiteId: draft.homeSiteId || null,
      status: draft.status,
    };
    const config = {
      organisationId,
      rosterGroupId: draft.rosterGroupId || null,
      startLocation: orNull(draft.startLocation),
      defaultStartTime: hms(draft.defaultStartTime),
      scheduleType: draft.scheduleType,
      workingPattern: draft.workingPattern,
      phaseEvenWeekStart: draft.phaseEvenWeekStart,
      jobType: null,
    };

    try {
      if (editingId === 'new') {
        const created = await createResource.mutateAsync({ organisationId, ...resourceFields });
        await upsertConfig.mutateAsync({ ...config, resourceId: created.id });
        showToast({ type: 'success', message: 'Driver added.' });
      } else {
        await updateResource.mutateAsync({ id: editingId, ...resourceFields });
        await upsertConfig.mutateAsync({ ...config, resourceId: editingId });
        showToast({ type: 'success', message: 'Driver updated.' });
      }
      cancel();
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error ? err.message : 'Failed to save driver.' });
    }
  };

  if (!organisationId) {
    return (
      <ErrorBanner
        title="No organisation"
        message="Couldn't resolve the current organisation — sign in again or pick an organisation, then retry."
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold text-slate-800">Drivers</h2>
        {missingCount > 0 && (
          <button
            type="button"
            onClick={() => setOnlyMissing((v) => !v)}
            aria-pressed={onlyMissing}
            className={
              'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ' +
              (onlyMissing
                ? 'border-slate-700 bg-slate-700 text-white'
                : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700')
            }
          >
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
            Not on roster
            <span className="opacity-60">({missingCount})</span>
          </button>
        )}
        <div className="flex-1" />
        <div className="relative">
          <Search
            className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <input
            type="text"
            placeholder="Search drivers…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-52 rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
        {editingId === null && (
          <button
            type="button"
            onClick={startNew}
            className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50"
          >
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        )}
      </div>

      {resourcesQuery.isError && (
        <ErrorBanner title="Failed to load drivers" message={resourcesQuery.error.message} />
      )}

      {selected.size > 0 && (
        <BulkRosterBar
          selectedCount={selected.size}
          newCount={selectedNewCount}
          values={bulk}
          groups={groups}
          sites={sites}
          patternLabels={PATTERN_LABELS}
          progress={bulkProgress}
          onChange={(patch) => setBulk((b) => ({ ...b, ...patch }))}
          onApply={() => void applyBulk()}
          onClear={clearSelection}
        />
      )}

      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full table-auto text-xs">
          <thead className="bg-slate-700 text-[11px] uppercase tracking-wide text-white">
            <tr>
              <th className="w-8 px-3 py-2 text-left font-semibold">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  onChange={toggleAllFiltered}
                  disabled={filtered.length === 0 || bulkProgress !== null}
                  aria-label="Select all drivers"
                  title="Select all (filtered)"
                  className="h-3.5 w-3.5 rounded border-slate-300"
                />
              </th>
              <th className="px-3 py-2 text-left font-semibold">Name</th>
              <th className="px-3 py-2 text-left font-semibold">Group</th>
              <th className="px-3 py-2 text-left font-semibold">Depot</th>
              <th className="px-3 py-2 text-left font-semibold">Start</th>
              <th className="px-3 py-2 text-left font-semibold">Location</th>
              <th className="px-3 py-2 text-left font-semibold">Schedule</th>
              <th className="px-3 py-2 text-left font-semibold">Pattern</th>
              <th className="px-3 py-2 text-right font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {editingId === 'new' && (
              <tr>
                <td colSpan={9} className="bg-slate-50 p-3">
                  <Editor
                    draft={draft}
                    groups={groups}
                    sites={sites}
                    isBusy={isBusy}
                    onChange={set}
                    onSave={() => void save()}
                    onCancel={cancel}
                  />
                </td>
              </tr>
            )}

            {filtered.length === 0 && editingId !== 'new' ? (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-xs italic text-slate-400">
                  {resourcesQuery.isPending
                    ? 'Loading…'
                    : q
                      ? 'No drivers match your search.'
                      : 'No drivers yet — use “Add”.'}
                </td>
              </tr>
            ) : (
              filtered.map((r) =>
                editingId === r.resourceId ? (
                  <tr key={r.resourceId}>
                    <td colSpan={9} className="bg-slate-50 p-3">
                      <Editor
                        draft={draft}
                        groups={groups}
                        sites={sites}
                        isBusy={isBusy}
                        onChange={set}
                        onSave={() => void save()}
                        onCancel={cancel}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr
                    key={r.resourceId}
                    className={selected.has(r.resourceId) ? 'bg-purple-50' : 'hover:bg-slate-50'}
                  >
                    <td className="px-3 py-1.5">
                      <input
                        type="checkbox"
                        checked={selected.has(r.resourceId)}
                        onChange={() => toggleRow(r.resourceId)}
                        disabled={bulkProgress !== null}
                        aria-label={`Select ${r.displayName || r.name}`}
                        className="h-3.5 w-3.5 rounded border-slate-300"
                      />
                    </td>
                    <td className="px-3 py-1.5 font-semibold text-slate-900">
                      <span className="inline-flex items-center gap-1.5">
                        {!r.hasConfig && (
                          <span
                            className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                            title="Not on the roster yet"
                            aria-hidden="true"
                          />
                        )}
                        {r.displayName || r.name}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-slate-600">{r.groupName ?? <Dash />}</td>
                    <td className="px-3 py-1.5 text-slate-600">
                      {r.homeSiteId ? (siteNameById.get(r.homeSiteId) ?? <Dash />) : <Dash />}
                    </td>
                    <td className="px-3 py-1.5 text-slate-600">{r.defaultStartTime ?? <Dash />}</td>
                    <td className="px-3 py-1.5 text-slate-600">{r.startLocation ?? <Dash />}</td>
                    <td className="px-3 py-1.5 text-slate-600">
                      {r.hasConfig ? (r.scheduleType === 'Casual' ? 'Casual' : 'Full time') : <Dash />}
                    </td>
                    <td className="px-3 py-1.5 text-slate-600">
                      {r.hasConfig ? PATTERN_LABELS[r.workingPattern] : <Dash />}
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <button
                        type="button"
                        aria-label={`Edit ${r.displayName || r.name}`}
                        disabled={editingId !== null}
                        onClick={() => startEdit(r)}
                        className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50 disabled:opacity-40"
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ),
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Dash() {
  return <span className="italic text-slate-300">—</span>;
}

function Editor({
  draft,
  groups,
  sites,
  isBusy,
  onChange,
  onSave,
  onCancel,
}: {
  draft: Draft;
  groups: { id: string; name: string }[];
  sites: { id: string; name: string }[];
  isBusy: boolean;
  onChange: (patch: Partial<Draft>) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="space-y-3">
      {/* Resource details — same shape as the Resources editor (no compliance). */}
      <div className={ROW}>
        <TextInput
          label={
            <>
              Name <span className="text-red-500">*</span>
            </>
          }
          name="name"
          value={draft.name}
          maxLength={256}
          onChange={(e) => onChange({ name: e.target.value })}
          disabled={isBusy}
        />
        <TextInput
          label="Display Name"
          name="displayName"
          value={draft.displayName}
          maxLength={128}
          onChange={(e) => onChange({ displayName: e.target.value })}
          disabled={isBusy}
        />
      </div>
      <div className={ROW}>
        <TextInput
          label="Licence Number"
          name="reference"
          value={draft.reference}
          maxLength={64}
          onChange={(e) => onChange({ reference: e.target.value })}
          disabled={isBusy}
        />
        <TextInput
          label="Mobile Number"
          name="mobileNumber"
          value={draft.mobileNumber}
          maxLength={32}
          placeholder="+353 87 000 0000"
          onChange={(e) => onChange({ mobileNumber: e.target.value })}
          disabled={isBusy}
        />
      </div>
      <div className={ROW}>
        <TextInput
          label="Email"
          name="email"
          type="email"
          value={draft.email}
          maxLength={320}
          onChange={(e) => onChange({ email: e.target.value })}
          disabled={isBusy}
        />
        <Switch
          label="Status"
          name="status"
          checked={draft.status === 'Active'}
          enabledLabel="Active"
          disabledLabel="Inactive"
          disabled={isBusy}
          onChange={(e) => onChange({ status: e.currentTarget.checked ? 'Active' : 'Inactive' })}
        />
      </div>

      {/* Roster config */}
      <div className="border-t border-slate-200 pt-3 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        Roster
      </div>
      <div className={ROW}>
        <SelectInput
          label="Group"
          name="group"
          value={draft.rosterGroupId}
          options={[
            { label: '— None —', value: '' },
            ...groups.map((g) => ({ label: g.name, value: g.id })),
          ]}
          onChange={(e) => onChange({ rosterGroupId: e.target.value })}
          disabled={isBusy}
        />
        <SelectInput
          label="Depot"
          name="depot"
          value={draft.homeSiteId}
          options={[
            { label: '— None —', value: '' },
            ...sites.map((s) => ({ label: s.name, value: s.id })),
          ]}
          onChange={(e) => onChange({ homeSiteId: e.target.value })}
          disabled={isBusy}
        />
      </div>
      <div className={ROW}>
        <TextInput
          label="Start time"
          name="startTime"
          type="time"
          value={draft.defaultStartTime}
          onChange={(e) => onChange({ defaultStartTime: e.target.value })}
          disabled={isBusy}
        />
        <TextInput
          label="Location"
          name="startLocation"
          value={draft.startLocation}
          maxLength={256}
          onChange={(e) => onChange({ startLocation: e.target.value })}
          disabled={isBusy}
        />
      </div>
      <div className={ROW}>
        <SelectInput
          label="Pattern"
          name="pattern"
          value={draft.workingPattern}
          options={PATTERNS.map((p) => ({ label: PATTERN_LABELS[p], value: p }))}
          onChange={(e) => onChange({ workingPattern: e.target.value as RosterWorkingPattern })}
          disabled={isBusy}
        />
        <Switch
          label="Schedule type"
          name="scheduleType"
          checked={draft.scheduleType === 'Casual'}
          enabledLabel="Casual"
          disabledLabel="Full time"
          disabled={isBusy}
          onChange={(e) => onChange({ scheduleType: e.currentTarget.checked ? 'Casual' : 'FullTime' })}
        />
        <Switch
          label="Even week start"
          name="phaseEvenWeekStart"
          checked={draft.phaseEvenWeekStart}
          enabledLabel="Yes"
          disabledLabel="No"
          disabled={isBusy}
          onChange={(e) => onChange({ phaseEvenWeekStart: e.currentTarget.checked })}
        />
      </div>

      <div className="flex items-center justify-end gap-1 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={isBusy}
          title="Cancel"
          aria-label="Cancel"
          className="flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-200 disabled:opacity-50"
        >
          <X className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={isBusy || draft.name.trim() === ''}
          title="Save"
          aria-label="Save"
          className="flex h-7 w-7 items-center justify-center rounded-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
        >
          {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}
